/**
 * One selectable row in a scope dropdown.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Check } from 'lucide-react';

import { ProviderBadge } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { ScopeOption } from './types';

export const ScopeOptionRow: FC<{
  option: ScopeOption;
  selected: boolean;
  onPick: (value: string) => void;
}> = ({ option: o, selected, onPick }) => (
  <button
    type="button"
    onClick={() => onPick(o.value)}
    title={o.title ?? o.label}
    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
  >
    {o.icon ? <span aria-hidden>{o.icon}</span> : null}
    {/* The name gets the room: basis-0 + grow means it takes what's left after
        the fixed-width id, instead of being squeezed out by a long UUID. */}
    <span
      className={cn('min-w-0 flex-1 basis-0 grow truncate', o.muted && 'text-muted-foreground')}
    >
      {o.label}
    </span>
    {o.provider ? <ProviderBadge provider={o.provider} noun="campaign" /> : null}
    {o.meta ? (
      <span className="shrink-0 whitespace-nowrap font-mono text-[10px] text-muted-foreground">
        {o.meta}
      </span>
    ) : null}
    {o.count ? (
      <span
        className="shrink-0 rounded-full bg-primary/12 px-1.5 text-[10px] font-semibold text-primary"
        title={`${o.count} condition${o.count === 1 ? '' : 's'} override this`}
      >
        {o.count}
      </span>
    ) : null}
    {selected ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden /> : null}
  </button>
);
